import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'primereact/button';

interface ITrainingSession {
  dayNumber?: number;
  workoutInstances?: unknown[];
}

interface ITrainingWeek {
  weekNumber?: number;
  trainingSessions?: ITrainingSession[];
}

export interface ICycleTemplate {
  id: number;
  name: string;
  duration: number;
  isDurationInMonths?: boolean;
  trainingWeeks: ITrainingWeek[];
}

interface ICycleCardProps {
  template: ICycleTemplate;
  onAssign: () => void;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const DAY_LABELS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

export function CycleCard({ template, onAssign, onView, onEdit, onDelete }: ICycleCardProps) {
  const intl = useIntl();

  const { totalSessions, avgPerWeek, week1ActiveDays } = useMemo(() => {
    const weeks = template.trainingWeeks ?? [];
    const total = weeks.reduce((sum, w) => sum + (w.trainingSessions?.length ?? 0), 0);
    const avg = weeks.length > 0 ? total / weeks.length : 0;
    const firstWeek = weeks[0];
    const active = new Set<number>();
    (firstWeek?.trainingSessions ?? []).forEach((s) => {
      if (typeof s.dayNumber === 'number') active.add(s.dayNumber);
    });
    return { totalSessions: total, avgPerWeek: avg, week1ActiveDays: active };
  }, [template.trainingWeeks]);

  const weekCount = template.trainingWeeks?.length ?? 0;
  const durationLabel = template.isDurationInMonths
    ? intl.formatMessage({ id: 'plansPage.card.cycleMonths' }, { count: template.duration })
    : intl.formatMessage({ id: 'plansPage.card.cycleWeeks' }, { count: template.duration });

  return (
    <div
      style={{
        background: 'var(--ios-card-bg)',
        border: '1px solid var(--ios-card-border)',
        borderRadius: 'var(--ios-radius-lg)',
        boxShadow: 'var(--ios-card-shadow)',
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        height: '100%',
        transition: 'var(--ios-transition)'
      }}
    >
      {/* Header: name + duration pill */}
      <div className="flex align-items-start justify-content-between gap-2">
        <h3
          style={{
            margin: 0,
            fontSize: '1.05rem',
            fontWeight: 700,
            letterSpacing: '-0.01em',
            color: 'var(--ios-text)',
            wordBreak: 'break-word',
            flex: 1,
            lineHeight: 1.2
          }}
        >
          {template.name}
        </h3>
        <span
          style={{
            padding: '0.2rem 0.6rem',
            background: 'rgba(163, 230, 53, 0.18)',
            color: '#4d7c0f',
            fontSize: '0.7rem',
            fontWeight: 700,
            borderRadius: 'var(--ios-radius-pill)',
            whiteSpace: 'nowrap'
          }}
        >
          {durationLabel}
        </span>
      </div>

      {/* Stats chips */}
      <div className="flex flex-wrap gap-2">
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.3rem',
            padding: '0.2rem 0.6rem',
            background: 'rgba(34,197,94,0.1)',
            color: '#22c55e',
            fontSize: '0.72rem',
            fontWeight: 600,
            borderRadius: 'var(--ios-radius-pill)'
          }}
        >
          <i className="pi pi-calendar" style={{ fontSize: '0.7rem' }} />
          {intl.formatMessage({ id: 'plansPage.card.cycleWeekCount' }, { count: weekCount })}
        </span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.3rem',
            padding: '0.2rem 0.6rem',
            background: 'rgba(245,158,11,0.1)',
            color: '#f59e0b',
            fontSize: '0.72rem',
            fontWeight: 600,
            borderRadius: 'var(--ios-radius-pill)'
          }}
        >
          <i className="pi pi-bolt" style={{ fontSize: '0.7rem' }} />
          {intl.formatMessage({ id: 'plansPage.card.cycleTotalSessions' }, { count: totalSessions })}
        </span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.3rem',
            padding: '0.2rem 0.6rem',
            background: 'rgba(14,165,233,0.1)',
            color: '#0ea5e9',
            fontSize: '0.72rem',
            fontWeight: 600,
            borderRadius: 'var(--ios-radius-pill)'
          }}
        >
          <i className="pi pi-chart-line" style={{ fontSize: '0.7rem' }} />
          {intl.formatMessage({ id: 'plansPage.card.cycleAvgPerWeek' }, { avg: avgPerWeek.toFixed(1) })}
        </span>
      </div>

      {/* Week 1 preview */}
      {weekCount > 0 && (
        <div className="flex flex-column gap-1">
          <span
            style={{
              fontSize: '0.65rem',
              fontWeight: 600,
              color: 'var(--ios-text-tertiary)',
              letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }}
          >
            {intl.formatMessage({ id: 'plansPage.card.cycleWeek1Preview' })}
          </span>
          <div className="flex gap-1">
            {DAY_LABELS.map((label, idx) => {
              const dayNumber = idx + 1;
              const active = week1ActiveDays.has(dayNumber);
              return (
                <div
                  key={dayNumber}
                  style={{
                    flex: 1,
                    height: '1.75rem',
                    borderRadius: 'var(--ios-radius-sm)',
                    background: active ? '#0a0a0a' : 'var(--ios-surface-muted)',
                    color: active ? '#fff' : 'var(--ios-text-tertiary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 700
                  }}
                >
                  {label}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Actions */}
      <div
        className="flex align-items-center justify-content-end gap-1"
        style={{
          borderTop: '1px solid var(--ios-divider)',
          paddingTop: '0.6rem',
          marginTop: 'auto'
        }}
      >
        <Button
          icon="pi pi-user-plus"
          className="p-button-text p-button-sm"
          tooltip={intl.formatMessage({ id: 'common.assign' })}
          tooltipOptions={{ position: 'top' }}
          onClick={onAssign}
          style={{ color: '#22c55e' }}
        />
        <Button
          icon="pi pi-eye"
          className="p-button-text p-button-sm"
          tooltip={intl.formatMessage({ id: 'common.view' })}
          tooltipOptions={{ position: 'top' }}
          onClick={onView}
          style={{ color: 'var(--ios-text-secondary)' }}
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-text p-button-sm"
          tooltip={intl.formatMessage({ id: 'common.edit' })}
          tooltipOptions={{ position: 'top' }}
          onClick={onEdit}
          style={{ color: 'var(--ios-text-secondary)' }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-text p-button-sm"
          tooltip={intl.formatMessage({ id: 'common.delete' })}
          tooltipOptions={{ position: 'top' }}
          onClick={onDelete}
          style={{ color: '#ef4444' }}
        />
      </div>
    </div>
  );
}
